import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import SEO from '../components/SEO';
import LandingNav from '../components/Layout/LandingNav';
import LandingFooter from '../components/Layout/LandingFooter';

function TermsOfService() {
  const { theme } = useTheme();
  const d = theme === 'dark';

  return (
    <div className={`min-h-screen flex flex-col ${d ? 'bg-[#0a0a0a]' : 'bg-white'}`}>
      <SEO
        title="Terms of Service"
        description="Read the PaperBrainz terms of service governing your use of our AI customer support chatbot platform."
        path="/terms-of-service"
      />
      <LandingNav />

      <div className="flex-1 pt-32 sm:pt-40 pb-24 sm:pb-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16 sm:mb-20 max-w-2xl mx-auto">
            <h1 className={`text-5xl md:text-6xl lg:text-[64px] font-semibold tracking-tight mb-5 ${d ? 'text-white' : 'text-gray-900'}`}>
              Terms of Service
            </h1>
            <p className={`text-lg md:text-xl font-light leading-relaxed ${d ? 'text-gray-500' : 'text-gray-500'}`}>
              Please read these terms carefully before using our AI chatbot service.
            </p>
            <p className={`text-sm font-light mt-4 ${d ? 'text-gray-600' : 'text-gray-400'}`}>
              Legal Entity: This service is operated by IE Aleksandr Kirakosyan ("Company", "we", "us", or "our").
            </p>
            <p className={`text-sm font-light mt-1 ${d ? 'text-gray-600' : 'text-gray-400'}`}>
              Last updated: March 26, 2026
            </p>
          </div>

          {/* Content */}
          <div className="space-y-14 sm:space-y-16">
            <div className="space-y-14 sm:space-y-16">

              {/* Acceptance of Terms */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  Acceptance of Terms
                </h2>
                <p className={`mb-4 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  By accessing and using PaperBrainz services, you accept and agree to be bound by the terms and provisions of this agreement.
                </p>
                <p className="text-gray-500 text-sm">
                  If you do not agree to abide by these terms, please do not use this service.
                </p>
              </section>

              {/* Service Description */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  Service Description
                </h2>
                <p className={`mb-4 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  PaperBrainz provides AI-powered customer support chatbot services, including:
                </p>
                <ul className={`list-disc pl-6 space-y-2 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  <li>AI chatbot training on your uploaded documents and knowledge base</li>
                  <li>Customizable chat widget for your website</li>
                  <li>Real-time analytics and conversation insights</li>
                  <li>24/7 automated customer support responses</li>
                  <li>Dashboard for managing your chatbot settings</li>
                </ul>
                <p className="text-gray-500 text-sm mt-4">
                  PaperBrainz is a fully automated service. All chatbot responses, AI processing, and data handling are performed by automated systems. Human support is available for customer inquiries, technical assistance, and account management.
                </p>
              </section>

              {/* Usage Rules */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  Usage Rules
                </h2>
                <p className={`mb-4 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  You may use our service for legitimate business purposes. Here are the allowed and prohibited uses:
                </p>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className={`rounded-[16px] p-5 ${d ? 'bg-white/[0.03]' : 'bg-gray-50'}`}>
                    <h4 className={`font-semibold mb-3 ${d ? 'text-white' : 'text-gray-900'}`}>Allowed Uses</h4>
                    <ul className={`space-y-2 text-sm ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                      <li>• Commercial websites and applications</li>
                      <li>• Customer support automation</li>
                      <li>• FAQ and knowledge base assistance</li>
                      <li>• Product information delivery</li>
                      <li>• Lead qualification and routing</li>
                    </ul>
                  </div>

                  <div className={`rounded-[16px] p-5 ${d ? 'bg-white/[0.03]' : 'bg-gray-50'}`}>
                    <h4 className={`font-semibold mb-3 ${d ? 'text-white' : 'text-gray-900'}`}>Prohibited Uses</h4>
                    <ul className={`space-y-2 text-sm ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                      <li>• Any illegal or unauthorized purposes</li>
                      <li>• Generating harmful or misleading content</li>
                      <li>• Reselling or redistributing the service</li>
                      <li>• Reverse engineering our service</li>
                      <li>• Excessive abuse or system manipulation</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Plan Limits */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  Plan Limits and Quotas
                </h2>
                <p className={`mb-4 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  All plans include usage limits to ensure fair use and service quality:
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse overflow-hidden rounded-xl">
                    <thead className={d ? 'bg-white/[0.03]' : 'bg-gray-50'}>
                      <tr>
                        <th className={`px-4 py-3 text-left text-sm font-medium border-b ${d ? 'text-white border-white/[0.06]' : 'text-gray-900 border-gray-200'}`}>Plan</th>
                        <th className={`px-4 py-3 text-left text-sm font-medium border-b ${d ? 'text-white border-white/[0.06]' : 'text-gray-900 border-gray-200'}`}>Monthly Messages</th>
                        <th className={`px-4 py-3 text-left text-sm font-medium border-b ${d ? 'text-white border-white/[0.06]' : 'text-gray-900 border-gray-200'}`}>Knowledge Sources</th>
                      </tr>
                    </thead>
                    <tbody className={d ? 'text-gray-400' : 'text-gray-600'}>
                      <tr className={`border-b ${d ? 'border-white/[0.06]' : 'border-gray-200'}`}>
                        <td className="px-4 py-3">Basic</td>
                        <td className="px-4 py-3">2,000</td>
                        <td className="px-4 py-3">5</td>
                      </tr>
                      <tr className={`border-b ${d ? 'border-white/[0.06]' : 'border-gray-200'}`}>
                        <td className="px-4 py-3">Pro</td>
                        <td className="px-4 py-3">10,000</td>
                        <td className="px-4 py-3">20</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Payment Terms */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  Payment Terms
                </h2>
                <p className={`mb-4 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  For paid plans, the following payment terms apply:
                </p>
                <ul className={`list-disc pl-6 space-y-2 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  <li>Monthly or annual billing cycle with automatic renewal</li>
                  <li>Charges processed automatically on renewal date via Lemon Squeezy</li>
                  <li>14-day refund policy on initial subscription purchase</li>
                  <li>Cancel anytime - access continues until end of billing period</li>
                  <li>Pro-rated adjustments for plan upgrades</li>
                </ul>
                <p className="text-sm text-gray-500 mt-4">
                  All payments are processed securely through Lemon Squeezy, our merchant of record. Please review our <Link to="/refund-policy" className={`underline underline-offset-2 decoration-1 transition-colors ${d ? 'text-white hover:text-gray-300' : 'text-gray-900 hover:text-gray-700'}`}>Refund Policy</Link> for complete details.
                </p>
              </section>

              {/* Data and Content */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  Your Data and Content
                </h2>
                <div className={`space-y-4 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  <p>
                    <span className={d ? 'text-gray-300' : 'text-gray-700'}>Ownership:</span> You retain full ownership of all content you upload to PaperBrainz, including documents, FAQs, knowledge base materials, and chat transcripts generated by your widget.
                  </p>
                  <p>
                    <span className={d ? 'text-gray-300' : 'text-gray-700'}>License:</span> By uploading content, you grant us a limited, non-exclusive license to store, process, and transmit this content solely to provide the chatbot service to you. This license ends when you delete the content or close your account.
                  </p>
                  <p>
                    <span className={d ? 'text-gray-300' : 'text-gray-700'}>No model training:</span> We do not use your content, chat transcripts, or end-user data to train PaperBrainz machine-learning models or any third-party foundation model. Our LLM provider (OpenAI) is configured with zero-retention / no-training terms.
                  </p>
                  <p>
                    <span className={d ? 'text-gray-300' : 'text-gray-700'}>Content responsibility:</span> You are responsible for ensuring you have the rights to upload and use any content you provide, including any personal data of third parties.
                  </p>
                  <p>
                    <span className={d ? 'text-gray-300' : 'text-gray-700'}>Website scraping warranty:</span> If you submit a URL to be scraped or crawled, you warrant that (a) you own the domain or are authorised by its owner to allow automated content extraction, and (b) the scraping does not violate that site's terms of service, robots.txt, or applicable law. You agree to indemnify us against any claim arising from URLs you submit.
                  </p>
                </div>
              </section>

              {/* AI Disclaimers */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  AI Response Disclaimers
                </h2>
                <p className={`mb-4 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  While we strive to provide accurate AI responses based on your knowledge base, we cannot guarantee 100% accuracy. The AI may occasionally produce incorrect or unexpected responses.
                </p>
                <p className={d ? 'text-gray-400' : 'text-gray-600'}>
                  Use of AI-generated responses is at your own discretion. We recommend reviewing your chatbot's responses and maintaining up-to-date knowledge base content for best results.
                </p>
              </section>

              {/* Service Availability */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  Service Availability
                </h2>
                <p className={`mb-4 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  We aim to provide reliable service with minimal downtime:
                </p>
                <ul className={`list-disc pl-6 space-y-2 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  <li><span className={d ? 'text-gray-300' : 'text-gray-700'}>99.9% Uptime Target:</span> We target high availability for all paid plans</li>
                  <li><span className={d ? 'text-gray-300' : 'text-gray-700'}>Scheduled Maintenance:</span> Occasional maintenance with advance notice when possible</li>
                  <li><span className={d ? 'text-gray-300' : 'text-gray-700'}>Service Updates:</span> We may update features and functionality to improve the service</li>
                </ul>
              </section>

              {/* Limitation of Liability */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  Limitation of Liability
                </h2>
                <p className={`mb-4 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  In no event shall PaperBrainz or IE Aleksandr Kirakosyan be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses.
                </p>
                <p className={d ? 'text-gray-400' : 'text-gray-600'}>
                  Our total liability to you for all claims shall not exceed the amount you paid us in the 12 months preceding the claim.
                </p>
              </section>

              {/* Termination */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  Termination
                </h2>
                <p className={`mb-4 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  Either party may terminate this agreement:
                </p>
                <ul className={`list-disc pl-6 space-y-2 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  <li>You may cancel your subscription at any time through your dashboard</li>
                  <li>We may terminate for violation of these terms</li>
                  <li>Upon termination, your data will be retained for 30 days before deletion</li>
                </ul>
              </section>

              {/* Changes to Terms */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  Changes to Terms
                </h2>
                <p className={d ? 'text-gray-400' : 'text-gray-600'}>
                  We may modify these terms at any time. We will provide notice of material changes via email or through our service. Continued use of the service constitutes acceptance of the new terms.
                </p>
              </section>

              {/* Key Points */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  Key Points
                </h2>
                <div className={`rounded-[16px] p-6 ${d ? 'bg-white/[0.03]' : 'bg-gray-50'}`}>
                  <ul className="space-y-3">
                    {[
                      'Commercial use allowed',
                      'Cancel subscription anytime',
                      '99.9% uptime target',
                      'You retain ownership of your content',
                      'Fair usage policy applies'
                    ].map((item, i) => (
                      <li key={i} className={`flex items-center ${d ? 'text-gray-300' : 'text-gray-700'}`}>
                        <span className={`w-1 h-1 rounded-full mr-3 flex-shrink-0 ${d ? 'bg-gray-500' : 'bg-gray-400'}`} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </section>

              {/* Governing Law */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  Governing Law and Disputes
                </h2>
                <p className={`mb-4 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  These Terms are governed by the laws of the Republic of Armenia, without regard to its conflict-of-laws principles. Any dispute arising from or relating to these Terms or the service will be brought exclusively in the competent courts of Yerevan, Armenia.
                </p>
                <p className={d ? 'text-gray-400' : 'text-gray-600'}>
                  If you are a consumer residing in the European Union, the United Kingdom, or another jurisdiction that grants mandatory consumer-protection rights, nothing in these Terms removes any rights you have under the law of your country of residence, and you may bring proceedings in the courts of that country.
                </p>
              </section>

              {/* Contact Information */}
              <section className={`border-t pt-8 ${d ? 'border-white/[0.06]' : 'border-gray-200'}`}>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${d ? 'text-white' : 'text-gray-900'}`}>
                  Contact Information
                </h2>
                <p className={`mb-4 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  For questions about these terms of service, please contact our team:
                </p>
                <div className={`rounded-[16px] p-6 ${d ? 'bg-white/[0.03]' : 'bg-gray-50'}`}>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className={`font-medium mb-2 ${d ? 'text-white' : 'text-gray-900'}`}>Company</h4>
                      <p className={d ? 'text-gray-400' : 'text-gray-600'}>IE Aleksandr Kirakosyan</p>
                      <p className="text-gray-500 text-sm mt-1">PaperBrainz Support Team</p>
                    </div>
                    <div>
                      <h4 className={`font-medium mb-2 ${d ? 'text-white' : 'text-gray-900'}`}>Contact</h4>
                      <p className={d ? 'text-gray-400' : 'text-gray-600'}>
                        <a href="mailto:support@paperbrainz.com" className={`underline underline-offset-2 decoration-1 transition-colors ${d ? 'text-white hover:text-gray-300' : 'text-gray-900 hover:text-gray-700'}`}>
                          support@paperbrainz.com
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
      <LandingFooter />
    </div>
  );
}

export default TermsOfService;
